<template>
  <section id="home" class="hero" :class="{ 'hero--has-image': hasImageBanner }">
    <div class="hero-backdrop" aria-hidden="true" />
    <el-carousel
      :key="carouselMountKey"
      class="hero-carousel"
      :class="{ 'hero-carousel--images': hasImageBanner }"
      :height="carouselHeight"
      :interval="2000"
      :autoplay="slides.length > 1"
      :loop="slides.length > 1"
      :pause-on-hover="false"
      :arrow="slides.length > 1 ? 'hover' : 'never'"
      trigger="click"
    >
      <el-carousel-item v-for="(slide, i) in slides" :key="slide.key || i">
        <div
          class="slide-inner"
          :class="{ 'slide-inner--media': !!slide.image }"
        >
          <div
            v-if="slide.image"
            class="slide-media"
            :style="{ backgroundImage: `url(${slide.image})` }"
            role="img"
            :aria-label="slide.title || '轮播图'"
          />
          <div v-if="slide.image" class="slide-scrim" aria-hidden="true" />
          <div class="slide-content">
            <p v-if="slide.tag" class="hero-tag">{{ slide.tag }}</p>
            <h1 v-if="slide.title" class="hero-title">{{ slide.title }}</h1>
            <p v-if="slide.subtitle" class="hero-subtitle">{{ slide.subtitle }}</p>
          </div>
        </div>
      </el-carousel-item>
    </el-carousel>
  </section>
</template>

<script>
import { fetchBanners } from '../api/banners'

const FALLBACK_SLIDES = [
  {
    key: 'fallback-1',
    image: '',
    tag: '图书馆 · 物联网 · 人工智能',
    title: '智慧图书馆整体解决方案',
    subtitle:
      '创造更便捷的人机交互与高品质阅读体验，以稳定可靠的产品和全周期服务，助力文化行业数字化升级。',
    primaryLabel: '了解解决方案',
    primaryHref: '#solutions',
    secondaryLabel: '产品中心',
    secondaryHref: '#products',
    secondaryTo: '/products'
  },
  {
    key: 'fallback-2',
    image: '',
    tag: '产品中心 · 感知 · 交互 · 自动化',
    title: '物联网与 AI 产品线一站交付',
    subtitle:
      '覆盖 RFID 与馆员工作站、智慧借阅与门禁、视觉盘点与空间导航、立体书库与分拣机器人，可按馆情组合部署。',
    primaryLabel: '查看产品',
    primaryHref: '#products',
    primaryTo: '/products',
    secondaryLabel: '联系售前',
    secondaryHref: '#contact'
  },
  {
    key: 'fallback-3',
    image: '',
    tag: '案例 · 服务 · 生态',
    title: '标杆项目验证的可靠体系',
    subtitle:
      '从公共馆、高校馆到城市书房与中小学阅读场景，以可复制的工程化交付与运维体系，保障长期稳定运行。',
    primaryLabel: '案例展示',
    primaryHref: '#cases',
    secondaryLabel: '关于我们',
    secondaryHref: '#about'
  }
]

export default {
  name: 'HeroSection',
  props: {
    prefetchSlides: {
      type: Array,
      default: () => []
    },
    disableRemoteFetch: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      slides: FALLBACK_SLIDES.map((s) => ({ ...s })),
      /** 接口替换轮播数据后重建 el-carousel，避免子项注册时反复 resetTimer 导致首屏好几秒才切页 */
      carouselMountKey: 0,
      hasTriedRemoteFetch: false
    }
  },
  computed: {
    hasImageBanner() {
      return this.slides.some((item) => !!item.image)
    },
    carouselHeight() {
      return this.hasImageBanner ? '100vh' : 'calc(78vh - 140px)'
    }
  },
  watch: {
    prefetchSlides: {
      immediate: true,
      handler(list) {
        if (!Array.isArray(list) || !list.length) return
        this.slides = list.map((s) => ({ ...s }))
        this.$nextTick(() => {
          this.carouselMountKey += 1
        })
      }
    },
    disableRemoteFetch(next) {
      if (next) return
      if (Array.isArray(this.prefetchSlides) && this.prefetchSlides.length) return
      this.loadRemoteSlides()
    }
  },
  methods: {
    async loadRemoteSlides() {
      if (this.hasTriedRemoteFetch) return
      this.hasTriedRemoteFetch = true
      try {
        const list = await fetchBanners()
        if (Array.isArray(list) && list.length > 0) {
          this.slides = list
          await this.$nextTick()
          this.carouselMountKey += 1
        }
      } catch (e) {
        console.warn('[Hero] banner API unavailable, using local fallback', e)
      }
    }
  },
  async mounted() {
    if (Array.isArray(this.prefetchSlides) && this.prefetchSlides.length) return
    if (this.disableRemoteFetch) return
    this.loadRemoteSlides()
  }
}
</script>

<style scoped>
.hero {
  position: relative;
  min-height: 78vh;
  padding: 88px 0 0;
  color: #0f172a;
  overflow: hidden;
}

.hero--has-image {
  min-height: 100vh;
  padding-top: 0;
}

.hero-backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
  background:
    radial-gradient(ellipse 90% 55% at 50% -10%, rgba(147, 197, 253, 0.45), transparent 50%),
    linear-gradient(165deg, #eef6ff 0%, #dbeafe 45%, #c7ddff 100%);
}

.hero-backdrop::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(125, 211, 252, 0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(125, 211, 252, 0.07) 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(ellipse 75% 70% at 50% 45%, #000 20%, transparent 75%);
  pointer-events: none;
}

.hero--has-image .hero-backdrop {
  opacity: 0;
  pointer-events: none;
}

.hero-carousel {
  position: relative;
  z-index: 1;
  width: 100%;
}

.hero--has-image .hero-carousel {
  min-height: 100vh;
}

.hero-carousel :deep(.el-carousel__container) {
  border-radius: 0;
}

.hero-carousel :deep(.el-carousel__item) {
  background-color: transparent;
  overflow: visible;
}

/* 内置指示器：叠在轮播图底部（勿用 indicator-position="outside"，会跑到图下方白底上） */
.hero-carousel--images :deep(.el-carousel__indicators--horizontal) {
  bottom: 28px;
}

.hero-carousel--images :deep(.el-carousel__indicator .el-carousel__button) {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.4);
}

.hero-carousel--images :deep(.el-carousel__indicator.is-active .el-carousel__button) {
  background-color: #38bdf8;
  box-shadow: 0 0 12px rgba(56, 189, 248, 0.65);
}

.hero-carousel:not(.hero-carousel--images) :deep(.el-carousel__indicator .el-carousel__button) {
  background-color: rgba(37, 99, 235, 0.25);
}

.hero-carousel:not(.hero-carousel--images) :deep(.el-carousel__indicator.is-active .el-carousel__button) {
  background-color: #2563eb;
}

.hero-carousel :deep(.el-carousel__arrow) {
  background-color: rgba(15, 23, 42, 0.35);
  color: #fff;
}

.hero-carousel :deep(.el-carousel__arrow:hover) {
  background-color: rgba(15, 23, 42, 0.55);
}

.hero-carousel :deep(.el-carousel__item) {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(78vh - 160px);
  box-sizing: border-box;
  padding: 0 20px;
}

.hero--has-image .hero-carousel :deep(.el-carousel__item) {
  min-height: 100vh;
  padding: 0;
}

.slide-inner {
  position: relative;
  width: 100%;
  max-width: 820px;
  text-align: center;
}

.slide-inner--media {
  max-width: none;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide-media {
  position: absolute;
  inset: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.slide-scrim {
  position: absolute;
  inset: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  background: linear-gradient(
    180deg,
    rgba(4, 26, 46, 0.45) 0%,
    rgba(4, 26, 46, 0.72) 55%,
    rgba(4, 26, 46, 0.88) 100%
  );
  pointer-events: none;
}

.slide-content {
  position: relative;
  z-index: 2;
  max-width: 820px;
  margin: 0 auto;
  padding: 0 8px;
}

.slide-inner--media .slide-content {
  padding-top: 88px;
  box-sizing: border-box;
}

.slide-inner--media .hero-subtitle {
  text-shadow: 0 2px 24px rgba(0, 0, 0, 0.45);
}

/* 轮播大图主标题：亮白 → 天青 → 冰蓝的渐变字 */
.slide-inner--media .hero-title {
  background: linear-gradient(
    115deg,
    #ffffff 0%,
    #e0f2fe 18%,
    #7dd3fc 42%,
    #38bdf8 58%,
    #bae6fd 82%,
    #f8fafc 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 3px 18px rgba(0, 0, 0, 0.5));
}

.hero-tag {
  font-size: 13px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #2563eb;
  margin-bottom: 20px;
  font-weight: 500;
}

.hero-title {
  font-size: clamp(32px, 5vw, 52px);
  margin-bottom: 22px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.02em;
}

.hero-subtitle {
  font-size: clamp(16px, 2.2vw, 19px);
  margin-bottom: 36px;
  opacity: 0.92;
  font-weight: 400;
  line-height: 1.75;
  color: #475569;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 28px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s, border-color 0.2s;
}

.btn-primary {
  background: #2563eb;
  color: #fff;
  box-shadow:
    0 4px 24px rgba(37, 99, 235, 0.35),
    0 0 0 1px rgba(147, 197, 253, 0.35);
}

.btn-primary:hover {
  background: #1d4ed8;
  color: #fff;
  box-shadow: 0 6px 28px rgba(37, 99, 235, 0.45);
}

.btn-ghost {
  background: transparent;
  color: #1e3a8a;
  border: 1px solid rgba(37, 99, 235, 0.45);
}

.btn-ghost:hover {
  background: rgba(37, 99, 235, 0.08);
  border-color: #2563eb;
}

.slide-inner--media .hero-tag,
.slide-inner--media .hero-subtitle,
.slide-inner--media .btn-ghost {
  color: #fff;
}

@media (max-width: 600px) {
  .hero:not(.hero--has-image) {
    padding-top: 76px;
  }

  .hero:not(.hero--has-image) .hero-carousel :deep(.el-carousel__container) {
    height: calc(70vh - 80px) !important;
  }

  .hero-carousel :deep(.el-carousel__item) {
    min-height: calc(70vh - 76px);
  }

  .hero--has-image .hero-carousel :deep(.el-carousel__item) {
    min-height: 100vh;
  }

  .slide-inner--media {
    min-height: calc(70vh - 76px);
  }

  .hero--has-image .slide-inner--media {
    min-height: 100vh;
  }

  .slide-inner--media .slide-content {
    padding-top: 76px;
  }
}
</style>
